import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { Invitation, RosterRef } from '../models/invitation.model';
import { InvitationMockService } from '../services/invitation-mock.service';
import { wireScheduleRules } from '../schedule-validators';

@Component({
  selector: 'app-invitation-edit',
  templateUrl: './invitation-edit.component.html',
  styleUrls: ['./invitation-edit.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InvitationEditComponent implements OnInit {
  /** Hosted in a modal from the listing / detail; falls back to the route param if absent. */
  @Input() invitationId?: string;
  @Output() saved = new EventEmitter<Invitation>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  rosters: RosterRef[] = [];
  id!: string;

  constructor(private fb: FormBuilder, private svc: InvitationMockService, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.id = this.invitationId ?? this.route.snapshot.paramMap.get('id')!;
    this.svc.getRosters().subscribe(r => this.rosters = r);
    this.form = this.fb.group({
      name: ['', Validators.required],
      rosterId: ['', Validators.required],
      language: ['English', Validators.required],
      estimatedParticipants: [1, [Validators.required, Validators.min(1)]],
      whoInvites: ['i_email', Validators.required],
      whoPays: ['i_pay', Validators.required],
      howResults: ['i_present', Validators.required],
      status: ['open', Validators.required],
      deadline: ['', Validators.required],
      presentation: ['', Validators.required],
      followupEmailsEnabled: [false],
      followupDate: [''],
    });
    this.svc.getById(this.id).subscribe(inv => {
      if (!inv) { this.cancelled.emit(); return; }
      const coaching = toDateInput(inv.schedule.find(s => s.name === 'followup')?.date);
      this.form.patchValue({
        name: inv.name, rosterId: inv.rosterId, language: inv.language,
        estimatedParticipants: inv.estimatedParticipants, whoInvites: inv.whoInvites,
        whoPays: inv.whoPays, howResults: inv.howResults, status: inv.status,
        deadline: toDateInput(inv.schedule.find(s => s.name === 'deadline')?.date),
        presentation: toDateInput(inv.schedule.find(s => s.name === 'presentation')?.date),
        followupEmailsEnabled: inv.followupEmailsEnabled,
        followupDate: coaching,
      });
      // Wire after patching: this form shares app-invitation-step-schedule (and its note stating
      // the rules), so it must enforce them too. Seeding `coachingAlreadySet` from the stored date
      // stops the autofill overwriting a date the organiser already chose.
      wireScheduleRules(this.form, { coachingAlreadySet: !!coaching });
    });
  }

  save(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    const v = this.form.value;
    const roster = this.rosters.find(r => r.id === v.rosterId);
    const schedule = [
      { id: 's1', step: 1, name: 'deadline', eventName: 'Participant deadline', date: toIso(v.deadline) },
      { id: 's2', step: 2, name: 'presentation', eventName: 'Presentation', date: toIso(v.presentation) },
    ];
    if (v.followupEmailsEnabled && v.followupDate) {
      schedule.push({ id: 's3', step: 3, name: 'followup', eventName: 'First follow-up email', date: toIso(v.followupDate) });
    }
    this.svc.update(this.id, {
      name: v.name, rosterId: v.rosterId, rosterName: roster?.name ?? '',
      language: v.language, estimatedParticipants: v.estimatedParticipants,
      whoInvites: v.whoInvites, whoPays: v.whoPays, howResults: v.howResults,
      status: v.status, followupEmailsEnabled: v.followupEmailsEnabled, schedule,
    }).subscribe(u => {
      Swal.fire({ title: 'Saved', icon: 'success', timer: 1100, showConfirmButton: false });
      this.saved.emit(u);
    });
  }
  cancel(): void { this.cancelled.emit(); }
}

// bsDatepicker works with Date objects; the model stores ISO strings.
function toDateInput(iso?: string): Date | null { return iso ? new Date(iso) : null; }
function toIso(d?: Date | string | null): string { return d ? new Date(d).toISOString() : ''; }
